import { useReducer } from "react";
import type { RefObject } from "react";
import { domToPng } from "modern-screenshot";
import { downloadPngDataUrl } from "@/features/common/helper";
import { restoreChartBarProperties } from "../charts-and-tables/chartBarSerialization";

interface State {
  isExporting: boolean;
  error: string | null;
}

type Action =
  | { type: "exportStart" }
  | { type: "exportSuccess" }
  | { type: "exportError"; error: string };

const initialState: State = {
  isExporting: false,
  error: null,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "exportStart":
      return { isExporting: true, error: null };
    case "exportSuccess":
      return { isExporting: false, error: null };
    case "exportError":
      return { isExporting: false, error: action.error };
    default:
      return state;
  }
};

interface UseExportToPngParams<T extends HTMLElement> {
  elementRef: RefObject<T>;
  fileTitle: string;
  errorMessage: string;
}

const isExportExcluded = (node: Node) =>
  node instanceof Element && node.classList.contains("tcw-export-exclude");

function collectAllElements(root: Element, out: HTMLElement[] = []): HTMLElement[] {
  if (root instanceof HTMLElement) out.push(root);
  const shadowRoot = (root as HTMLElement).shadowRoot;
  if (shadowRoot) {
    Array.from(shadowRoot.children).forEach((child) => collectAllElements(child, out));
  }
  Array.from(root.children).forEach((child) => collectAllElements(child, out));
  return out;
}

function measureWidthDeficit(elements: HTMLElement[]): number {
  let maxDeficit = 0;
  for (const el of elements) {
    const deficit = el.scrollWidth - el.clientWidth;
    if (deficit > maxDeficit) maxDeficit = deficit;
  }
  return maxDeficit;
}

interface StencilElement extends HTMLElement {
  componentOnReady?: () => Promise<HTMLElement | undefined>;
}

async function waitForCustomElements(root: Element): Promise<void> {
  const elements = Array.from(root.querySelectorAll<StencilElement>("*"));
  await Promise.all(
    elements
      .filter((el) => typeof el.componentOnReady === "function")
      .map((el) => el.componentOnReady!()),
  );
}

async function withTrueContentWidth<T>(
  wrapper: HTMLElement,
  run: (clone: HTMLElement, trueWidth: number) => Promise<T>,
): Promise<T> {
  const liveElements = collectAllElements(wrapper);
  const trueWidth = Math.ceil(wrapper.scrollWidth + measureWidthDeficit(liveElements)) + 2;

  const clone = wrapper.cloneNode(true) as HTMLElement;
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "0";
  container.style.width = "0";
  container.style.height = "0";
  container.style.overflow = "hidden";
  container.style.pointerEvents = "none";
  container.appendChild(clone);
  document.body.appendChild(container);

  try {
    restoreChartBarProperties(clone);
    await waitForCustomElements(clone);
    collectAllElements(clone).forEach((el) => {
      el.style.overflow = "visible";
      el.style.maxWidth = "none";
      if (el.classList.contains("tcw-chart-label")) el.style.whiteSpace = "nowrap";
    });
    clone.style.width = `${trueWidth}px`;
    clone.style.margin = "0";
    Array.from(clone.children).forEach((child) => {
      if (child instanceof HTMLElement) child.style.margin = "0";
    });
    await new Promise((resolve) => setTimeout(resolve, 150));
    return await run(clone, trueWidth);
  } finally {
    container.remove();
  }
}

export function useExportToPng<T extends HTMLElement>({
  elementRef,
  fileTitle,
  errorMessage,
}: UseExportToPngParams<T>) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleExport = async () => {
    if (!elementRef.current) return;

    dispatch({ type: "exportStart" });
    await new Promise((resolve) => setTimeout(resolve, 200));

    try {
      const dataUrl = await withTrueContentWidth(elementRef.current, (clone, trueWidth) =>
        domToPng(clone, {
          scale: 2,
          backgroundColor: "#ffffff",
          width: trueWidth,
          height: Math.ceil(clone.getBoundingClientRect().height),
          filter: (node) => !isExportExcluded(node),
        }),
      );
      downloadPngDataUrl(dataUrl, fileTitle);
      dispatch({ type: "exportSuccess" });
    } catch (error) {
      console.error("Failed to export chart/table as PNG:", error);
      dispatch({ type: "exportError", error: errorMessage });
    }
  };

  return {
    isExporting: state.isExporting,
    error: state.error,
    handleExport,
  };
}
